import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ServicingFacilitiesPacimService } from '@core/service';
import { isDefined } from '@shared/utility';
import { phoneNumberValidator } from '@shared/validators';
import { noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { ServicingFacilityContactDetailsPAC } from '@shared/model';
import { isEmpty } from 'lodash-es';
import { filter, finalize, take } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'servicing-facility-contact-details',
    templateUrl: './servicing-facility-contact-details.component.html',
    styleUrls: ['./servicing-facility-contact-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ServicingFacilityContactDetailsComponent implements OnInit, OnDestroy {
  @Input() contactDetails: ServicingFacilityContactDetailsPAC;
  @Input() isExternal!: boolean;

  @Output() detailsUpdated = new EventEmitter<ServicingFacilityContactDetailsPAC>(true);

  panelDescription = '';

  form: FormGroup;
  loading = false;

  private readonly externalRequiredFields = [
    'contactName',
    'contactPhone',
    'contactFax'
  ];
  private readonly subs = new SubSink();

  constructor(
    private fb: FormBuilder,
    private facilitiesService: ServicingFacilitiesPacimService
  ) {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.createForm();
    this.setFormUpdate();

    if (isEmpty(this.contactDetails) && this.isExternal) {
      this.getUserDetails();
    } else {
      this.form.patchValue({ ...this.contactDetails });
    }

    this.panelDescription = this.isExternal
      ? 'Please provide the contact information for a Carelon representative to reach out to you or a delegate, if necessary.'
      : 'If available, enter the contact name and phone of a representative to contact if necessary.'
  }

  addMaxLengthValidator(): void {
    this.form.get('contactName').addValidators(Validators.maxLength(100));
    this.form.get('contactName').updateValueAndValidity();
  }

  private createForm(): void {
    this.form = this.fb.group({
      contactName: [null, noWhiteSpaceValidator],
      contactPhone: [null, phoneNumberValidator],
      contactPhoneExtension: [null, Validators.maxLength(9)],
      contactFax: [null, phoneNumberValidator]
    });

    if (this.isExternal) {
      this.externalRequiredFields.forEach(field => {
        this.form.get(field).addValidators(Validators.required);
      });
    }
  }

  private getUserDetails(): void {
    this.loading = true;

    this.subs.sink = this.facilitiesService.getCurrentUserDetails().pipe(
      take(1),
      filter(details => isDefined(details)),
      finalize(() => this.loading = false),
    ).subscribe(details => {
      this.form.patchValue({
        contactFax: details.preferredBusinessFax,
        contactName: `${details.firstName} ${details.lastName}`,
        contactPhone: details.preferredBusinessPhone,
        contactPhoneExtension: details.businessPhoneExtension
      });
    });
  }

  private setFormUpdate(): void {
    this.subs.sink = this.form.valueChanges.subscribe(() => {
      this.detailsUpdated.emit(this.form.valid ? this.form.value : null);
    });
  }
}
